import { useRef, useEffect, useCallback } from "react"
import { motion, useInView } from "motion/react"

/**
 * Shared scroll-triggered sequence hook.
 * Runs a timed sequence of state updates when the element is in view,
 * resets when it leaves.
 */
export function useScrollSequence(
    sequence: Array<{ delay: number }>,
    onStep: (index: number) => void,
    reset: () => void,
    inViewOptions: { amount?: number; margin?: string } = {},
) {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { ...inViewOptions, once: false } as Parameters<typeof useInView>[1])
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const indexRef = useRef(0)

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    useEffect(() => {
        clearTimer()

        if (!inView) {
            reset()
            indexRef.current = 0
            return
        }

        indexRef.current = 0

        const runSequence = () => {
            if (indexRef.current >= sequence.length) return
            const { delay } = sequence[indexRef.current]
            const stepIdx = indexRef.current
            indexRef.current += 1
            timerRef.current = setTimeout(() => {
                onStep(stepIdx)
                if (indexRef.current < sequence.length) runSequence()
            }, delay)
        }

        runSequence()
        return clearTimer
    }, [inView]) // eslint-disable-line react-hooks/exhaustive-deps

    return { ref, inView }
}

/**
 * Animated thinking dots — pulsing opacity, matches product chat style.
 */
export function ThinkingDots() {
    return (
        <span className="inline-flex items-center gap-1 h-5">
            {[0, 1, 2].map((i) => (
                <motion.span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-neutral-400/60"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: i * 0.2,
                    }}
                />
            ))}
        </span>
    )
}
